// src/components/AudioControls.tsx
import React, { memo, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import RecordingScreen from "./RecordingScreen";

interface AudioControlsProps {
  isRecording: boolean;
  recordStartTime: number | null;
  audioBlob: Blob | null;
  audioProcessed: boolean;
  processing: boolean;
  backendAvailable: boolean;
  statusMsg: string;
  claudeApiKey: string;
  patientData: any;
  transcriptionData: any;
  analysis: string;
  startRecording: () => void;
  stopRecording: () => void;
  transcribeAudio: (blob: Blob) => void;
  generateHandoff: () => void;
}

const AudioControls: React.FC<AudioControlsProps> = memo(({
  isRecording,
  recordStartTime,
  audioBlob,
  audioProcessed,
  processing,
  backendAvailable,
  statusMsg,
  claudeApiKey,
  patientData,
  transcriptionData,
  analysis,
  startRecording,
  stopRecording,
  transcribeAudio,
  generateHandoff
}) => {
  const { theme } = useTheme();
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const [showRecordingScreen, setShowRecordingScreen] = useState(false);

  // Dynamic background based on theme
  const backgroundColor = theme === 'dark' ? '#121826' : '#ffffff';

  const handleStartRecording = () => {
    setShowRecordingScreen(true);
    startRecording();
  };

  const handleStopRecording = () => {
    stopRecording();
    setShowRecordingScreen(false);
  };

  return (
    <>
      {/* Full-screen recording overlay - rendered at portal level */}
      {showRecordingScreen && isRecording && (
        <RecordingScreen onStop={handleStopRecording} />
      )}

      <div className="audio-controls" style={{ backgroundColor }}>
        <div className="button-row">
          <button 
            className="record-btn"
            onClick={handleStartRecording}
            disabled={processing}
          >
            Record
          </button>

          <button
            className="generate-handoff-btn"
            onClick={generateHandoff}
            disabled={
              processing ||
              !backendAvailable ||
              !claudeApiKey ||
              !patientData
            }
          >
            {processing ? "Generating..." : "Generate Handoff"}
          </button>
        </div>
      </div>

      {audioBlob && (
        <div className="audio-section" style={{ backgroundColor }}>
          <h4>Recorded Audio</h4>
          <audio 
            ref={audioPlayerRef} 
            controls 
            src={URL.createObjectURL(audioBlob)} 
          />
          {!audioProcessed && !processing && (
            <button
              className="transcribe-btn"
              onClick={() => transcribeAudio(audioBlob)}
              disabled={!backendAvailable}
            >
              Transcribe Audio
            </button>
          )}
        </div>
      )}

      {/* Only show status messages that are NOT handoff-related (those are shown in AnalysisSection) */}
      {statusMsg && !statusMsg.includes('handoff') && !statusMsg.includes('Handoff') && 
       !statusMsg.includes('Analysis') && !statusMsg.includes('Changes saved') && 
       !statusMsg.includes('Displaying generated') && !statusMsg.includes('Contacting the') &&
       !statusMsg.includes('Agent') && (
        <div className="recording-status" style={{ backgroundColor }}>
          {statusMsg}
        </div>
      )}
    </>
  );
});

AudioControls.displayName = 'AudioControls';

export default AudioControls;